import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { ShieldCheck, ScrollText, MessageCircle } from 'lucide-react';

// Renders a styled <h2> with a bottom border like the rest of the legal pages.
const Section = ({ title, children }) => (
  <>
    <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">{title}</h2>
    {children}
  </>
);

const P = ({ children }) => (
  <p className="text-slate-600 leading-relaxed mb-4">{children}</p>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3 text-slate-600 leading-relaxed mb-2">
    <span className="w-1.5 h-1.5 rounded-full bg-teal-500 flex-shrink-0 mt-2.5" />
    <span>{children}</span>
  </li>
);

export const CancellationRefund = () => {
  const updated = '29 April 2026';

  return (
    <div className="min-h-screen" data-testid="cancellation-refund-page">
      <SEO
        title="Cancellation & Refund Policy"
        description="K&K Medical and Dental cancellation and refund policy for GP and dental consultations, in line with the South African Consumer Protection Act and HPCSA ethical guidelines."
        keywords="cancellation policy, refund policy, HPCSA, CPA, K&K Medical, no-show fee, South Africa"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 to-teal-50/40 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold uppercase tracking-widest mb-4">
            <ScrollText className="w-4 h-4" />
            Legal & Policies
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-3">
            Cancellation &amp; Refund Policy
          </h1>
          <p className="text-slate-600">K&amp;K Medical and Dental &middot; Last updated {updated}</p>
        </div>
      </section>

      {/* Scope callout */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border border-teal-100 bg-teal-50 rounded-2xl p-6 flex gap-4">
            <ShieldCheck className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-slate-900 mb-1">Scope</p>
              <p className="text-slate-700 leading-relaxed">
                This policy applies to <strong>General Practitioner (GP) consultations and Dental services</strong> provided by K&amp;K Medical and Dental
                at both our Umzinto and Scottburgh branches. It does <strong>not</strong> apply to medical specialists, third-party laboratory fees,
                or services rendered by external providers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="pb-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          <Section title="1. Introduction">
            <P>
              K&amp;K Medical and Dental ("the Practice", "we", "us") is committed to fair, transparent, and lawful billing in line with the
              <strong> Consumer Protection Act 68 of 2008 ("CPA")</strong>, the <strong>Health Professions Council of South Africa ("HPCSA")</strong> ethical
              guidelines, and the National Health Act. This policy sets out when fees apply for cancelled or missed appointments, how refunds are handled,
              and how to raise a dispute.
            </P>
            <P>
              By booking an appointment, attending a consultation, or making payment, you acknowledge that you have read and understood this policy.
            </P>
          </Section>

          <Section title="2. Definitions">
            <ul className="space-y-1 mb-4">
              <Bullet><strong>"Walk-in"</strong> — an unbooked GP consultation attended on a first-come, first-served basis during published operating hours.</Bullet>
              <Bullet><strong>"Booked appointment"</strong> — any consultation (GP, dental, weight-loss, surgical pre-booked slot) confirmed in advance by WhatsApp, telephone or in person.</Bullet>
              <Bullet><strong>"Late cancellation"</strong> — a cancellation received less than the notice period set out in Section 4.</Bullet>
              <Bullet><strong>"No-show"</strong> — failure to attend a booked appointment without any prior notification.</Bullet>
              <Bullet><strong>"Overpayment"</strong> — a payment exceeding the amount lawfully owed, including duplicate payments.</Bullet>
            </ul>
          </Section>

          <Section title="3. Payment for Services">
            <P>
              Consultation fees are payable at the time of service. Once a consultation has been provided, fees are generally{' '}
              <strong>non-refundable</strong>, except where:
            </P>
            <ul className="space-y-1 mb-4">
              <Bullet>an overpayment or duplicate payment is confirmed;</Bullet>
              <Bullet>a refund is required by the Consumer Protection Act or other applicable law;</Bullet>
              <Bullet>the Practice agrees in writing to issue a refund; or</Bullet>
              <Bullet>the Practice was unable to honour a booked appointment (see Section 6).</Bullet>
            </ul>
            <P className="text-sm text-slate-500">
              In line with the CPA, a blanket "no refund" rule cannot override any statutory right the consumer holds — this policy does not, and is not
              intended to, limit those rights.
            </P>
          </Section>

          <Section title="4. Cancellation Notice Periods">
            <P>
              You are entitled to cancel any booked appointment at any time. Late cancellations and no-shows may, however, attract a reasonable cancellation
              fee in line with section 17 of the CPA and current HPCSA ethical guidance.
            </P>

            <div className="grid sm:grid-cols-2 gap-4 my-6">
              <div className="border border-slate-200 rounded-2xl p-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-2">General Practitioner</p>
                <p className="text-lg font-bold text-slate-900">2-hour notice</p>
                <p className="text-slate-600 text-sm mt-1">
                  Cancel a booked GP appointment at least <strong>two (2) hours</strong> before the scheduled time to avoid any cancellation fee.
                </p>
              </div>
              <div className="border border-slate-200 rounded-2xl p-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-2">Dental Services</p>
                <p className="text-lg font-bold text-slate-900">24-hour notice</p>
                <p className="text-slate-600 text-sm mt-1">
                  Cancel a booked dental appointment at least <strong>twenty-four (24) hours</strong> before the scheduled time to avoid any cancellation fee.
                </p>
              </div>
            </div>

            <P>
              <strong>Walk-in GP visits are not affected by this policy</strong> as no advance booking is made.
            </P>
          </Section>

          <Section title="5. When We May Charge a Cancellation or No-Show Fee">
            <P>A reasonable fee may be charged only if all of the following conditions are met:</P>
            <ul className="space-y-1 mb-4">
              <Bullet>The cancellation is received <strong>less than the applicable notice period</strong> in Section 4 above, or you fail to attend at all;</Bullet>
              <Bullet>The Practice was unable, despite reasonable efforts, to fill the appointment slot with another patient;</Bullet>
              <Bullet>You were informed of this cancellation policy at the time the appointment was made (verbally, by WhatsApp, by signage, or on this website); and</Bullet>
              <Bullet>The reason for your non-attendance is not death, hospitalisation, or other circumstances expressly protected under section 17(5) of the CPA.</Bullet>
            </ul>
            <P>
              Cancellation and no-show fees are intentionally kept modest and will <strong>never exceed the cost of the consultation</strong> that would
              have been charged had you attended. They are not, in themselves, a full consultation fee.
            </P>
          </Section>

          <Section title="6. When the Practice Cancels an Appointment">
            <P>
              In line with section 47 of the CPA, if the Practice is unable to honour your booked appointment we will:
            </P>
            <ul className="space-y-1 mb-4">
              <Bullet>contact you as soon as reasonably practicable;</Bullet>
              <Bullet>offer to reschedule at your convenience, free of charge; and</Bullet>
              <Bullet>refund any amount you have already paid for the appointment.</Bullet>
            </ul>
            <P>
              Where the cancellation is due to circumstances beyond our reasonable control (e.g. medical emergency, doctor illness, force majeure) and
              we have notified you as soon as practicable, refunds will be limited to the amount actually paid (excluding any incidental costs).
            </P>
          </Section>

          <Section title="7. Refund Process & Timeframes">
            <P>
              All refund requests must be submitted via email to{' '}
              <a href="mailto:refunds@kkmedical.co.za" className="text-teal-600 hover:text-teal-700 underline font-medium">
                refunds@kkmedical.co.za
              </a>{' '}
              — this is the only channel through which the Practice handles refund and billing disputes.
            </P>
            <P>Your email should include:</P>
            <ul className="space-y-1 mb-4">
              <Bullet>full name and contact number;</Bullet>
              <Bullet>proof of payment (receipt or bank statement extract);</Bullet>
              <Bullet>date and branch of the appointment;</Bullet>
              <Bullet>a clear description of the reason for the refund request; and</Bullet>
              <Bullet>your bank account details for the refund.</Bullet>
            </ul>
            <P>
              Once received, a practice manager will verify the payment and appointment record, review the request, and notify you in writing of the
              outcome. Approved refunds are paid back via the original payment method where possible, or by EFT to the verified bank account.
            </P>
            <div className="border border-teal-200 bg-teal-50 rounded-xl p-4 my-4">
              <p className="text-slate-700">
                <strong>Refund timeframe:</strong> approved refunds are processed within <strong>fourteen (14) calendar days</strong> of confirmation. This
                mirrors the HPCSA institutional standard for verified overpayments. We will notify you when the refund has been released.
              </p>
            </div>
          </Section>

          <Section title="8. Refunds & Medical Aid">
            <P>
              If your appointment was billed directly to your medical aid scheme, refunds will be processed back to the medical aid where required by
              scheme rules and tariff agreements. Where you paid the practice directly (e.g. co-payment, cash, card), the refund will be issued to you on
              the terms set out in Section 7.
            </P>
            <P>
              You remain responsible for any portion of the fee not covered by your medical aid scheme.
            </P>
          </Section>

          <Section title="9. Clinical Disputes vs Billing Disputes">
            <P>
              We distinguish between two types of complaints:
            </P>
            <ul className="space-y-1 mb-4">
              <Bullet><strong>Billing or refund disputes</strong> — handled internally under this policy and, if necessary, escalated to the National Consumer Commission or the relevant Ombud.</Bullet>
              <Bullet><strong>Clinical or ethical disputes</strong> (e.g. concerns about quality of care) — handled through the Practice's internal clinical complaints process and, where appropriate, the HPCSA's professional conduct procedures. Where clinically appropriate, we may offer a follow-up consultation at no charge in place of a refund.</Bullet>
            </ul>
          </Section>

          <Section title="10. How to Raise a Dispute">
            <P>
              All refund and billing disputes must be raised <strong>in writing by email</strong> within{' '}
              <strong>thirty (30) days</strong> of the event. This is the only channel through which the Practice handles refund and billing matters,
              so that we can keep a clear written record of every request and respond efficiently.
            </P>
            <div className="border border-teal-200 bg-teal-50 rounded-xl p-5 my-4 text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-1">Refund / Dispute Email</p>
              <a
                href="mailto:refunds@kkmedical.co.za"
                className="text-2xl font-bold text-teal-700 hover:text-teal-800 underline"
                data-testid="refunds-email-link"
              >
                refunds@kkmedical.co.za
              </a>
            </div>
            <P>
              If the matter is not resolved to your satisfaction, you retain the right to escalate the matter to:
            </P>
            <ul className="space-y-1 mb-4">
              <Bullet><strong>National Consumer Commission</strong> &mdash; for breaches of the Consumer Protection Act.</Bullet>
              <Bullet><strong>HPCSA</strong> &mdash; for ethical or professional conduct concerns about a registered practitioner.</Bullet>
              <Bullet>The relevant <strong>Ombud</strong> or your <strong>medical aid scheme</strong> for scheme-related billing matters.</Bullet>
            </ul>
          </Section>

          <Section title="11. Recordkeeping">
            <P>
              The Practice keeps written records of appointment bookings, cancellation notifications, attempts to fill cancelled slots, payment receipts,
              and refund decisions for at least five (5) years, in line with the Health Professions Act and HPCSA recordkeeping rules.
            </P>
          </Section>

          <Section title="12. Updates to this Policy">
            <P>
              We may update this policy from time to time to reflect changes in the law or our internal procedures. The current version, dated above, is
              always available at <Link to="/cancellation-refund-policy" className="text-teal-600 hover:text-teal-700 underline">www.kkmedical.co.za/cancellation-refund-policy</Link>.
            </P>
          </Section>

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-slate-50 border border-slate-200 p-6 text-center">
            <p className="text-slate-700 mb-4">
              Questions about this policy or need to raise a refund request?
            </p>
            <a
              href="mailto:refunds@kkmedical.co.za?subject=Refund%20%2F%20Cancellation%20Enquiry"
              className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6 py-3 font-medium transition-colors"
              data-testid="cancellation-email-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Email refunds@kkmedical.co.za
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
